import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import useSearchStore from '../../_global/js/SearchStore';

export default function EnhancedSelect({ selectOptions, componentId }) {
    // Semi-unique ID for this component, used for accessiblity IDs and labels and returned in the optional callback
    // Set or create a new component ID
    const COMPONENT_ID =
        componentId || `enhancedSelect${Math.ceil(Math.random() * 10000)}`;

    const searchSortingValue = useSearchStore(
        (state) => state.searchSortingValue
    );
    const setSearchSortingValue = useSearchStore(
        (state) => state.setSearchSortingValue
    );

    /**
     * Respond to keyboard presses within the context of the popup container
     *
     * @param {KeyboardEvent} event Event that triggered the click
     *
     * @returns void
     */
    const onKeyPress = useCallback((event) => {
        if (event.key === 'ArrowUp') {
            // eslint-disable-next-line no-use-before-define
            switchElementFocus('up');
        }

        if (event.key === 'ArrowDown') {
            // eslint-disable-next-line no-use-before-define
            switchElementFocus('down');
        }

        if (event.key === 'Escape') {
            // Close the popup
            // eslint-disable-next-line no-use-before-define
            togglePopupMenu(false);
        }
    }, []);

    /**
     * Respond to clicks on the hidden popup background
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onBackgroundClick = useCallback((event) => {
        event.stopPropagation();

        // eslint-disable-next-line no-use-before-define
        if (event.target === popupOverlayBackground.current) {
            // Close the popup
            // eslint-disable-next-line no-use-before-define
            togglePopupMenu(false);
        }
    }, []);

    const popupOverlayBackground = useRef(null);
    const openPopupButtonElement = useRef(null);
    const selectPopupElement = useRef(null);
    const selectPopupContentElement = useRef(null);

    const [popupOptions, setPopupOptions] = useState([]);
    const [openPopupButtonExpanded, setOpenPopupButtonExpanded] =
        useState(false);
    const [
        openPopupButtonActiveDescendant,
        setOpenPopupButtonActiveDescendant,
    ] = useState('');
    const [
        selectOptionPopupAdditionalClasses,
        setSelectOptionPopupAdditionalClasses,
    ] = useState('');
    const [currentSelectedOption, setCurrentSelectedOption] = useState({
        label: '',
        value: '',
        elementId: '',
    });
    const [focusElementId, setFocusElementId] = useState(null);
    const [enableHiddenOverlay, setEnableHiddenOverlay] = useState(false);

    /**
     * Operations to perform when the component first mounts.
     * These only run once - the first time the component has been added.
     */
    useEffect(() => {
        // Listen for key presses (scoped to the popup)
        selectPopupElement.current.addEventListener(
            'keydown',
            onKeyPress,
            false
        );

        // Listen for clicks (scoped to the hidden overlay background)
        popupOverlayBackground.current.addEventListener(
            'click',
            onBackgroundClick,
            false
        );

        return () => {
            // Remove the listeners when component removed from DOM
            if (selectPopupElement.current !== null) {
                selectPopupElement.current.removeEventListener(
                    'keydown',
                    onKeyPress,
                    false
                );
            }
            if (popupOverlayBackground.current !== null) {
                popupOverlayBackground.current.removeEventListener(
                    'click',
                    onBackgroundClick,
                    false
                );
            }
        };
    }, []);

    /**
     * Listen for changes to the options to use for
     * the Enhanced Select.
     *
     * Update the provided options with an ID and
     * update the local state with the new options.
     */
    useEffect(() => {
        setPopupOptions(
            selectOptions.map((option, index) => {
                return {
                    label: option.label,
                    value: option.value,
                    elementId: `${COMPONENT_ID}-${index}`,
                };
            })
        );
    }, [selectOptions]);

    /**
     * Operations to perform when the popup options
     * have been populated.
     */
    useEffect(() => {
        // Set the default value (if any has been provided)
        const initialOptionConfiguration = popupOptions.filter((option) => {
            if (option.value === searchSortingValue) {
                return option;
            }
            return false;
        });

        if (initialOptionConfiguration.length === 1) {
            setCurrentSelectedOption({
                label: initialOptionConfiguration[0].label,
                value: initialOptionConfiguration[0].value,
                elementId: initialOptionConfiguration[0].elementId,
            });
        }
    }, [popupOptions]);

    /**
     * Operations to perform after the focus element has changed
     */
    useEffect(() => {
        // Locate the element in the DOM and set the keyboard focus on it
        if (focusElementId !== '' && focusElementId !== null) {
            document.getElementById(focusElementId).focus();
        }
    }, [focusElementId]);

    /**
     * Operations to perform after the shared sorting value
     * has been changed.
     */
    useEffect(() => {
        // Lookup the ID and label of the selected option
        const selectedOptionData = popupOptions.filter((option) => {
            if (option.value === searchSortingValue) {
                return option;
            }
            return false;
        });

        // Update the local state with the selected option
        // Check if the selection option is different to the current one
        if (
            selectedOptionData.length === 1 &&
            currentSelectedOption.elementId !== selectedOptionData[0].elementId
        ) {
            setCurrentSelectedOption({
                label: selectedOptionData[0].label,
                value: selectedOptionData[0].value,
                elementId: selectedOptionData[0].elementId,
            });
        }
    }, [searchSortingValue]);

    /**
     * Toggle the open state of the popup menu.
     *
     * Some inline style adjustments need to be made
     * to position the popup correctly.
     *
     * @param {Boolean} setOpen True will open the popup menu
     *
     * @returns void
     */
    function togglePopupMenu(setOpen) {
        if (setOpen) {
            // Toggle the invisible overlay
            setEnableHiddenOverlay(true);

            // Open the popup
            setSelectOptionPopupAdditionalClasses('is-active');

            // Set the keyboard focus on the currently selected option/button
            setFocusElementId(currentSelectedOption.elementId);
        } else {
            // Toggle the invisible overlay
            setEnableHiddenOverlay(false);

            // Close the popup
            setSelectOptionPopupAdditionalClasses('');

            // Reset some accessibility attributes
            setOpenPopupButtonExpanded(false);
            setOpenPopupButtonActiveDescendant('');
        }
    }

    /**
     * Move the keyboard focus up or down the DOM tree
     *
     * @param {String} direction "up" or "down"
     *
     * @returns void
     */
    function switchElementFocus(direction) {
        // Get the current element in focus
        const currentFocusElement = document.activeElement;
        let switchOptionParent = null;

        // If it's an option from our popup then switch focus to the next option in the list
        if (
            currentFocusElement.className.indexOf(
                'js-enhanced-select-option'
            ) !== -1
        ) {
            if (direction === 'up') {
                // Find the previous element in the list
                switchOptionParent =
                    currentFocusElement.parentNode.previousElementSibling;
            } else {
                // Find the next element in the list
                switchOptionParent =
                    currentFocusElement.parentNode.nextElementSibling;
            }

            // We don't loop back around, we stop at the last option in the list
            if (switchOptionParent !== null) {
                switchOptionParent
                    .getElementsByClassName('js-enhanced-select-option')[0]
                    .focus();
            }
        }
    }

    /**
     * Respond to clicks on the button that opens the popup menu.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onSelectOpenClick = (evt) => {
        evt.preventDefault();

        // Set some accessibility attributes
        setOpenPopupButtonExpanded(true);

        setOpenPopupButtonActiveDescendant(currentSelectedOption.elementId);

        // Open the popup
        togglePopupMenu(true);
    };

    /**
     * Respond to clicks on the option button(s).
     * Capture the new value and close the popup.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onSelectOptionClick = (evt) => {
        const selectedValue =
            evt.currentTarget.getAttribute('data-option-value');

        evt.preventDefault();

        // Close the popup
        togglePopupMenu(false);

        // Update the shared state with the new value
        setSearchSortingValue(selectedValue);
    };

    return (
        <>
            <div
                className={`enhanced-select__overlay-background ${
                    enableHiddenOverlay ? 'is-active' : ''
                }`}
                ref={popupOverlayBackground}
            />
            <div className="enhanced-select__container enhanced-select--narrow no-wysiwyg">
                <span className="sr-only">
                    {/* eslint-disable-next-line prettier/prettier */}
                    Click the following button to make a new selection. Your current selection is:
                </span>
                <button
                    aria-controls={`${COMPONENT_ID}listbox`}
                    aria-expanded={openPopupButtonExpanded}
                    aria-activedescendant={openPopupButtonActiveDescendant}
                    aria-haspopup="listbox"
                    className="enhanced-select__button"
                    onClick={onSelectOpenClick}
                    ref={openPopupButtonElement}
                    role="combobox"
                    tabIndex="0"
                    type="button"
                >
                    {/* eslint-disable-next-line prettier/prettier */}
                    <span className="enhanced-select__button-label">{currentSelectedOption.label}</span>
                    <svg className="svg-icon">
                        <use xlinkHref="#rse-chevron-down-12" fill="none" />
                    </svg>
                </button>
                <div
                    className={`enhanced-select-popup__container ${selectOptionPopupAdditionalClasses}`}
                    id={`${COMPONENT_ID}listbox`}
                    ref={selectPopupElement}
                    role="listbox"
                    tabIndex="-1"
                >
                    <span className="sr-only">
                        Press the Escape key to close this popup.
                    </span>
                    <div
                        className="enhanced-select-popup__list-wrapper"
                        ref={selectPopupContentElement}
                    >
                        <ul className="enhanced-select-popup__list">
                            {popupOptions.map((option) => {
                                const optionIsSelected =
                                    option.value ===
                                    currentSelectedOption.value;

                                let additionalClasses = '';

                                if (optionIsSelected) {
                                    additionalClasses = 'selected';
                                }

                                return (
                                    <li
                                        key={option.elementId}
                                        className="enhanced-select-popup__list-item"
                                    >
                                        <button
                                            aria-selected={optionIsSelected}
                                            className={`js-enhanced-select-option enhanced-select-popup__option ${additionalClasses}`}
                                            data-option-value={option.value}
                                            id={option.elementId}
                                            onClick={onSelectOptionClick}
                                            role="option"
                                            type="button"
                                        >
                                            {option.label}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
}

EnhancedSelect.propTypes = {
    selectOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        })
    ),
    componentId: PropTypes.string,
};

EnhancedSelect.defaultProps = {
    selectOptions: [
        {
            label: 'Select an option',
            value: '',
        },
        {
            label: 'Early Childhood Educator',
            value: 'ece',
        },
        {
            label: 'Malesuada Ipsum Vulputate Cursus',
            value: 'mivc',
        },
        {
            label: 'XYZ',
            value: 'xyz',
        },
        {
            label: 'School Leader',
            value: 'sl',
        },
        {
            label: 'Year Level Coordinator',
            value: 'ylc',
        },
        {
            label: 'Subject Coordinator',
            value: 'sc',
        },
        {
            label: 'Learner/Novice Driver',
            value: 'lnd',
        },
    ],
    componentId: null,
};
